<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { onMounted, watch } from 'vue';
import type { Location } from 'vue-router';
import { getModule } from 'vuex-module-decorators';
import { useRouter } from 'vue-router/composables';
import {
  DagAssetOverview,
  DashboardAssetOverview,
  DatasetAssetOverview,
  AssetService,
  TransformationAssetOverview, UserUiDto,
  MetadataJobsService,
  DescriptionPredictionFeedbackDto,
  PredictionFeedbackDto,
  AssetDetailsDto,
  LineageEntityDto,
  type AccessTokenProviderDto,
  type FieldDto,
  type DeclaredAssetDetailsDto,
  type AuthorizedAssetHeaderDto,
  type UnauthorizedAssetHeaderDto,
  type AssetDataProductDto,
  DataProductService,
} from '@/api';
import formatDate from '@/utils/filters/formatDate';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import Tags from '@/components/tags/Tags.vue';
import Terms from '@/components/terms/Terms.vue';
import fromNow from '@/utils/filters/fromNow';
import authModule, { canWithAssetLevelPermission, type AssetPermissionEntityType } from '@/store/modules/auth';
import store from '@/store';
import SRichTextViewer from '@/components/SRichTextViewer.vue';
import SqlDialog from '@/components/sql-dialog/Sql-Dialog.vue';
import ModalDialog from '@/components/modal-dialog/Modal-Dialog.vue';
import i18n from '@/i18n';
import EditDialog from '@/components/generic-edit-dialog/GenericEditDialog.vue';
import { getProviderIconSrc } from '@/utils/provider-icons';
import copyToClipboard from '@/utils/copyToClipboard';
import SIconComposer from '@/components/SIconComposer/SIconComposer.vue';
import CustomMetadataDisplay from '@/components/custom-metadata/CustomMetadataDisplay.vue';
import { type GenericEntity } from '@/components/generic-entity/@types/GenericEntityTypes';
import DataProductsDrawer from '@/modules/data-products/DataProductsDrawer.vue';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';
import SModal from '@/components/SModal.vue';
import { usePlans, PlanTypes } from '@/composables/plans';
import DataUsageExtended from '@/components/usage/Usage-Extended.vue';

interface DeleteItem {
  id: string;
  name: string;
  entityKey: string;
}

interface AssetOverviewDetailProps {
  urn: string;
  overviewData: DagAssetOverview | DashboardAssetOverview | DatasetAssetOverview | TransformationAssetOverview | null;
  assetData: AuthorizedAssetHeaderDto | UnauthorizedAssetHeaderDto | null;
  isSmall?: boolean;
  sourceUri?: string;
  sourceId?: string;
}

type AssetOverviewDetailEmits = {
  (event: 'update'): void
}

const auth = getModule(authModule, store);
const router = useRouter();
const plans = usePlans();
const emit = defineEmits(["update"]);
const props = defineProps({
  urn: null,
  overviewData: null,
  assetData: null,
  isSmall: { type: Boolean, default: false },
  sourceUri: null,
  sourceId: null
});

const EMPTY_STRING_VALUES = ['', '<p><br></p>'];

let isPlanLoaded = _ref(false);
let hasCatalogEditorPermission = _ref(false);
const sqlDialogRef = _ref<SqlDialog | null>(null);
const modalDialogRef = _ref<ModalDialog | null>(null);
const editDialogRef = _ref<InstanceType<typeof EditDialog> | null>(null);
let isMetadataGenerationEnabled = _ref<boolean>(true);
let customEditPredictionDialogTitle = _ref('');
let selectedDataProduct = _ref<GenericEntity | null>(null);
const deleteModal = _ref<InstanceType<typeof SModal> | null>(null);
let itemToDelete = _ref<DeleteItem>({} as DeleteItem);

const canHaveSuggestions = _computed(() => hasCatalogEditorPermission.value);

const descriptionPrediction = _computed(() => {
  const prediction = props.overviewData?.details?.descriptionPrediction;
  return prediction && prediction.validationStatus === DescriptionPredictionFeedbackDto.validationStatus.NO_FEEDBACK
    ? prediction
    : null;
});

const assetOverview = _computed(() => props.overviewData || {});
const assetDetails = _computed(() => assetOverview.value.details as Partial<DeclaredAssetDetailsDto> || {});
const predictionEditDescription = _computed(() => descriptionPrediction.value?.description ?? '');
const actionable = _computed(() => props.overviewData?.details?.actionable);
const canEdit = _computed(() => hasCatalogEditorPermission.value);
const details = _computed(() => props.overviewData?.details);
const orchestratorsDetails = _computed(() => assetDetails.value?.orchestratorsDetails || []);
const lastAccessDate = _computed(() => (details.value?.usage?.lastAccessDate ? fromNow(details.value?.usage?.lastAccessDate) : '-'));
const usage = _computed(() => details.value?.usage);
const tagsValue = _computed(() => details.value?.tags ?? []);
const termsValue = _computed(() => details.value?.terms ?? []);
const domainsValue = _computed(() => details.value?.domains ?? []);
const dataProductsValue = _computed(() => details.value?.dataProducts ?? []);
const ownersValue = _computed(() => details.value?.owners ?? []);
const descriptionValue = _computed(() => (details.value?.description && !EMPTY_STRING_VALUES.includes(details.value.description)
    ? details.value.description
    : '\\-'));
const isEmptyDescription = _computed(() => descriptionValue.value === '\\-');
const isSystemAdmin = _computed(() => auth.getUserRole === UserUiDto.role.ADMIN);
const isSystemEditor = _computed(() => auth.getUserRole === UserUiDto.role.SYSTEM_EDITOR);
const canPreview = _computed(() => hasCatalogEditorPermission.value);
const externalLinks = _computed(() => (props.assetData && 'externalLinks' in props.assetData ? props.assetData.externalLinks ?? [] : []));
const editDescriptionPredictionTooltip = _computed(() => (!canEdit.value ? i18n.t('app.rights.suggestions_no_rights') : null));
const isDeclaredAsset = _computed(() => props.assetData?.entityType === LineageEntityDto.entityType.DECLARED_ASSET);
const isDeclaredSource = _computed(() => assetDetails.value.sourceCreationType === AssetDetailsDto.sourceCreationType.DECLARED);
const createdBy = _computed(() => assetDetails.value.createdBy as AccessTokenProviderDto);
const lastModifiedBy = _computed(() => assetDetails.value?.lastModifiedBy as AccessTokenProviderDto);
const hasIntegrationDetails = _computed(() => details.value?.integrationName && details.value?.integrationId);

const hasPreview = _computed(() => {
  // By default, the preview feature is enabled, some clients wants to disable it
  const shouldShowPreview = isPlanLoaded.value ? plans.getDataCatalogPlan.value?.isPreviewEnabled ?? true : true;
  return props.overviewData?.details?.hasPreview && shouldShowPreview;
});

const transformation = _computed(() => {
  const d: any = details.value as any;
  return d?.transformation ?? null;
});

const integrationRoute = _computed<Location>(() => ({
  name: 'sources.source',
  params: { id: details.value?.integrationId || '' },
}));
// Note: this is a Vue 2 permission check for route access.
// When migrating to Vue 3, should be replaced with 'canAccessRoute' from PermissionsService,
// so that route permissions are defined in the router config only.
const hasAccessToIntegrationRoute = _computed(() => auth.userActions['integration.datasource.read']);

const hasTransformation = _computed(() => !!transformation.value);
const hasUsage = _computed(() => usage.value && 'qualification' in usage.value);

const externalDescriptionsValue = _computed(() => {
  if (!props.overviewData) return null;
  return ('externalDescriptions' in props.overviewData.details!)
    ? props.overviewData.details!.externalDescriptions : null;
});

const routeToOverview = _computed<Location>(() => ({
  name: 'data-catalog.asset.overview',
  params: { urn: props.urn },
}));

const routeToDomain = (domainId: string) => ({
  name: 'domains.domain.edit',
  params: { id: domainId },
});

const addDescription = async () => {
  const promise = AssetService.sendPredictionFeedback({
    urn: props.urn,
    requestBody: {
      predictionId: descriptionPrediction.value!.id,
      feedbackStatus: PredictionFeedbackDto.feedbackStatus.VALIDATED,
    },
  });
  await promise;
  emit('update');
};

const dismissDescription = async () => {
  const promise = AssetService.sendPredictionFeedback({
    urn: props.urn,
    requestBody: {
      predictionId: descriptionPrediction.value!.id,
      feedbackStatus: PredictionFeedbackDto.feedbackStatus.REJECTED,
    },
  });
  await promise;
  emit('update');
};

const getMetadataJobSettings = async () => {
  const metadataJobSettings = (await MetadataJobsService.getMetadataJobsSettings());
  isMetadataGenerationEnabled.value = metadataJobSettings.metadataGenerationEnabled!;
};

const openSuggestionDialog = (description: string, title: string) => {
  customEditPredictionDialogTitle.value = title;
  editDialogRef.value!.setEntities([{ description }]);
};

const openDescriptionPredictionEdit = () => {
  openSuggestionDialog(predictionEditDescription.value, i18n.t('data-catalog.nested_schema.prediction_edit_title', { name: props.assetData?.name }));
};

const editDialogUpdateHandler = async ({ patches }: { patches: FieldDto[] }) => {
  await AssetService.patchAsset({
    urn: props.urn,
    requestBody: { description: patches[0].description },
  });
  emit('update');
};

const setSelectedDataProduct = (dataProduct: AssetDataProductDto) => {
  selectedDataProduct.value = {
    id: dataProduct.id,
    name: dataProduct.name,
  } as GenericEntity;
};

const handleCloseDataProductsDrawer = () => {
  selectedDataProduct.value = null;
};

const handleCopyToClipboard = (value: string) => {
  copyToClipboard(value, i18n.t('assets.copied_to_clipboard', { value }));
};

const truncateDatasourceName = (value: string) => (value.length > 40 ? `${value.slice(0, 40)}...` : value);

const handleDeleteDataProduct = (dataProduct: GenericEntity) => {
  itemToDelete.value = {
    id: dataProduct.id,
    name: dataProduct.name,
    entityKey: 'data_products',
  } as DeleteItem;
  deleteModal.value?.openModal();
};

const handleUpdateDataProduct = (dataProduct: GenericEntity) => {
  router.push({
      name: 'data-products.data-product.edit',
      params: {
        id: String(dataProduct.id),
      },
    });
};

const onConfirmDeleteItem = () => {
  if (!itemToDelete.value) return;

  switch (itemToDelete.value.entityKey) {
    case 'data_products':
      DataProductService.deleteDataProduct({ id: itemToDelete.value.id })
        .then(() => {
          handleCloseDataProductsDrawer();
          emit('update');
        });
      break;
    default:
        null;
  }

  itemToDelete.value = {} as DeleteItem;
};

const openPreview = () => modalDialogRef.value?.openDialog();
const closePreview = () => modalDialogRef.value?.closeDialog();

watch(
  () => [props.assetData?.id, props.assetData?.entityType] as const,
  async ([id, entityType]) => {
    if (!id || !entityType) {
      hasCatalogEditorPermission.value = false;
      return;
    }
    hasCatalogEditorPermission.value = await canWithAssetLevelPermission('Catalog editor', entityType as AssetPermissionEntityType, id);
    if (hasCatalogEditorPermission.value) {
      await getMetadataJobSettings();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await plans.refreshPlan(PlanTypes.DATA_CATALOG);
  isPlanLoaded.value = true;
});
</script>

<template lang="pug">
SCard.my-4( :outlined="!isSmall" :title="!isSmall ? $t('assets.details') : ''")
  template(v-if="!overviewData")
    .d-flex.align-center(v-for="index in 11" :key="index").mt-9
      v-skeleton-loader.v-skeleton-loader-override.mr-10(
        type="list-item"
        width="30%"
        max-width="300px"
       )
      v-skeleton-loader.v-skeleton-loader-override.mr-10(
        type="list-item"
        width="60%"
      )
  template( v-else)
    .d-flex.justify-space-between
      .btn-container( v-if="isSmall" )
        .d-flex
          SButton.mr-2(
              v-if="actionable"
              icon="icon-book"
              :text="$t('assets.tabs.overview')"
              :tooltip="canEdit ? null : $t('app.rights.suggestions_no_rights')"
              :to="routeToOverview"
              color="secondary"
              variant="outlined"
              size="small"
            )

          template( v-if="isDeclaredAsset" )
            SThreeDotMenu.mr-2( size="small" variant="text" )
              SMenuButton(
                v-if="sourceUri"
                @click="handleCopyToClipboard(sourceUri)"
                icon="icon-copy"
                :text="$t('data-catalog.copy_uri')"
                size="small"
              )

          template( v-else )
            SButton.mr-2(
              v-if="canPreview && hasPreview && actionable"
              :tooltip="$t('assets.preview_data')"
              icon="icon-eye"
              variant="outlined"
              color="secondary"
              @click="openPreview"
              size="small")

            SButton.mr-2(
              v-if="actionable && !isDeclaredAsset && hasTransformation"
              icon="icon-document-sql"
              :tooltip="$t('assets.view_sql')"
              @click="sqlDialogRef?.open()"
              color="secondary"
              variant="outlined"
              size="small")

          SButton.mr-2(
            v-for="link in externalLinks"
            :key="link.link"
            color="secondary"
            variant="outlined"
            size="small"
            :tooltip="$t('assets.view_in', { datasource: link.lineagePlatformLabel || link.lineagePlatform })"
            :to="link.link" target="_blank" depressed )
            template(#icon)
              SIconComposer(icon="icon-new-window" :image-src="getProviderIconSrc(link.lineagePlatform)")

    v-divider.my-4( v-if="isSmall" )

    div.mb-5(v-if="isDeclaredAsset" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.ingestion_type') }}
        span.d-flex(slot="input" :class="{'flex-column align-start' : isSmall}")
          .d-flex.align-center
            SIcon(icon="icon-arrow-right-circle-fill" size="small" color="iconNeutral")
            span.ml-1.grey--text {{ $t('assets.declarative_asset') }}

    .mb-5( v-if="hasIntegrationDetails" )
       SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ isDeclaredSource ? $t('assets.declarative_source') : $t('assets.source') }}
        template(v-slot:input)
          .d-flex.pt-2.align-center
            SIcon( :icon="getProviderIconSrc(details?.lineagePlatform)" size="small")
            RouterLink.link.max-width-80.ml-1( v-if="hasAccessToIntegrationRoute" :to="integrationRoute" )
              SEllipsisTooltip( :content="details?.integrationName || ''" )
            SEllipsisTooltip( v-else :content="details?.integrationName || ''" )

    .mb-5( v-if="orchestratorsDetails.length" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.orchestrator') }}
        template(v-slot:input)
          .d-flex.pt-2.align-center.max-width-80(v-for="orchestrator in orchestratorsDetails" :key="orchestrator.urn")
            SIcon( :icon="getProviderIconSrc(orchestrator.platform)" size="small")
            RouterLink.link.ml-1( :to="{ name: 'data-catalog.asset.overview', params: { urn: orchestrator.urn } }" class="text-truncate max-width-200" v-if="(isSystemAdmin || isSystemEditor) && orchestrator.urn" ) {{ truncateDatasourceName(orchestrator.name ?? '') }}
            p.ml-1.mb-0(v-else) {{ truncateDatasourceName(orchestrator.name ?? '') }}

    v-row.mb-5(:class="{ 'mb-2 flex-column align-start': isSmall }" no-gutters)
      v-col.pt-2(style="max-width: 300px")
        SLabel {{ $t('assets.description') }}
      v-col.pt-2(:cols="isSmall ? 12 : 9" :class="{ 'mt-2': isSmall, 'align-left': true }")
        .description
          ExternalDescriptions.mb-2(
            v-if="!isDeclaredAsset && externalDescriptionsValue"
            :descriptions="externalDescriptionsValue" should-show-richtext)
          SuggestionsCard(
            v-if="!isDeclaredAsset && isMetadataGenerationEnabled && descriptionPrediction && isEmptyDescription && !isSmall"
            icon="icon-sparkles"
            :add-handler="addDescription"
            :dismiss-handler="dismissDescription"
            :disable-add="!canHaveSuggestions"
            :disable-dismiss="!canHaveSuggestions"
            :add-button-tooltip="$t('app.rights.suggestions_no_rights')"
            :dismiss-button-tooltip="$t('app.rights.suggestions_no_rights')"
            :tooltip-label="$t('assets.suggestion')"
            :label="descriptionPrediction.description")
            template(v-slot:extra-actions)
              SButton.mr-2(
                @click="openDescriptionPredictionEdit"
                icon="icon-edit"
                color="secondary"
                :disabled="!canHaveSuggestions"
                size="small"
                variant="text"
                :tooltip="editDescriptionPredictionTooltip" )
          SRichTextViewer.grey--text.viewer(v-else :content="descriptionValue")

    .mb-5
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.tags') }}
        .pt-2(slot="input")
          Tags( v-if="tagsValue.length" :tags="tagsValue" )
          span( v-else ) -

    .mb-5
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.business_terms') }}
        .pt-2(slot="input")
          Terms( v-if="termsValue.length" :terms="termsValue" )
          span( v-else ) -

    .mb-5
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.domains') }}
        .pt-2.d-flex.flex-wrap.domains(slot="input" v-if="domainsValue.length")
          .d-flex.align-center(v-for="domain in domainsValue" :key="domain.id")
            SIcon.mr-1(icon="icon-building" size="small" color="iconNeutral")
            RouterLink.text-decoration-none(:to="routeToDomain(domain.id)" v-if="isSystemAdmin" ) {{ domain.name }}
            p.mb-0(v-else) {{ domain.name }}
        span( v-else slot="input") -

    .mb-5
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.data_products') }}
        .pt-2.d-flex.flex-wrap.domains(slot="input" v-if="dataProductsValue.length")
          .d-flex.align-center(v-for="dataProduct in dataProductsValue" :key="dataProduct.id" @click.stop.prevent="setSelectedDataProduct(dataProduct)")
            SIcon.mr-1(icon="icon-data-product" size="small" color="iconNeutral")
            SEllipsisTooltip.link(:content="dataProduct.name")
        .pt-2.d-flex.align-center(v-else slot="input")
          span.grey--text -

    .mb-5
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.owners') }}
        .pt-2(slot="input")
          SAvatars( v-if="ownersValue.length" :users="ownersValue" wrapped size="20" data-track="owners" )
          span( v-else ) -

    .mb-5( v-if="!isDeclaredAsset && hasUsage" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px")
        .pt-2(slot="default")
          span {{ $t('assets.data_usage') }}
          v-tooltip( top max-width="200" )
            template(v-slot:activator='{ on }')
              span(v-on="on")
                SIcon.ml-2( icon="icon-question-circle-outline" v-on="on" size="small" color="iconNeutral")
            span {{ $t('data-catalog.usage_tooltip') }}
            div.tooltip-arrow-bottom
        .pt-2(slot="input")
          DataUsageExtended.pt-2( :value="usage" )

    .mb-5( v-if="!isDeclaredAsset && hasUsage" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px")
        .pt-2(slot="default")
          span {{ $t('assets.last_access_date') }}
          v-tooltip( top max-width="200" )
            template(v-slot:activator='{ on }')
              span(v-on="on")
                SIcon.ml-2( icon="icon-question-circle-outline" v-on="on" size="small" color="iconNeutral")
            span {{ $t('assets.last_access_date_tooltip') }}
            div.tooltip-arrow-bottom
        .pt-4(slot="input")
          span.grey--text {{ lastAccessDate }}

    .mb-5( v-if="isDeclaredAsset" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.created_by') }}
        span.pt-2.d-flex(slot="input" :class="{'flex-column align-start' : isSmall}")
            .d-flex.align-center
              SIcon(icon="icon-key" size="small" color="iconNeutral")
              span.ml-1.grey--text {{ createdBy?.name }}
              span.ml-4.grey--text {{  fromNow(formatDate(assetDetails.creationDate || '')) }}

    .mb-5( v-if="isDeclaredAsset" )
      SLabel(:as-columns="!isSmall"  label-max-width="300px") {{ $t('assets.edit_by') }}
        span.pt-2.d-flex(slot="input" :class="{'flex-column align-start' : isSmall}")
            .d-flex.align-center
              SIcon(icon="icon-key" size="small" color="iconNeutral")
              span.ml-1.grey--text {{ lastModifiedBy?.name }}
              span.ml-4.grey--text {{  fromNow(formatDate(assetDetails.lastModifiedDate || '')) }}

    .mb-5( v-if="details?.customMetadata" )
      CustomMetadataDisplay.mb-5(
        :custom-metadata="details?.customMetadata"
        :is-small="isSmall"
        has-narrow-label
      )

  ModalDialog(
    ref="modalDialogRef"
    :title="$t('common.words.preview')"
    :disableCancel="true" full )

    template( v-slot:body )
      AssetPreview(:urn="urn")

    template( v-slot:actions )
      .text-end
        v-btn(
          color='primary'
          depressed
          @click='closePreview') {{ $t('common.words.close') }}

  SqlDialog( v-if="hasTransformation" ref="sqlDialogRef" :sql-statement="transformation")
  EditDialog(
    ref="editDialogRef"
    :fields="['description']"
    :customTitle="customEditPredictionDialogTitle"
    has-rich-text
    @update="editDialogUpdateHandler")

  DataProductsDrawer(
    :is-open="!!selectedDataProduct"
    :item="selectedDataProduct"
    @delete-item="handleDeleteDataProduct"
    @update-item="handleUpdateDataProduct"
    @on-cancel="handleCloseDataProductsDrawer")

  SModal(
    ref="deleteModal"
    :can-save="true"
    :on-save="onConfirmDeleteItem"
    :save-label="$t('common.words.delete')"
    :title="$tc(`${itemToDelete.entityKey}.delete_confirm_title`, 1)"
    remove-dividers
    save-color="danger"
    save-icon="icon-trash"
    width="400"
    data-track="items-delete-modal-content")
      p.grey--text.mr-auto.text-subtitle-1 {{ $t(`${itemToDelete.entityKey}.delete_confirm_description`) }}

</template>

<style lang="scss" scoped>
.btn-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
}

.copy {
  cursor: pointer;

  &.v-icon:focus::after {
    opacity: 0 !important;
  }
}

.viewer {
  max-height: 400px;
}

.description {
  max-width: 600px;
}

.domains {
  gap: 8px 16px;
}

.max-width-80 {
  max-width: 80%;
}

.link {
  color: var(--v-primary-base);
  cursor: pointer;
}
</style>
